import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, BACKEND_URL } from '../../App';
import { coverUrl, COVER_WIDTH } from '../../lib/coverUrl';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Copy, Download, Send, CheckCircle, Loader2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const PreviewPublication = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [settings, setSettings] = useState({});
  
  // Cover replacement state
  const [showReplaceCoverDialog, setShowReplaceCoverDialog] = useState(false);
  const [newCoverFile, setNewCoverFile] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef(null);
  
  // Book file replacement state
  const [showReplaceBookDialog, setShowReplaceBookDialog] = useState(false);
  const [newBookFile, setNewBookFile] = useState(null);
  const [uploadingBook, setUploadingBook] = useState(false);
  const bookInputRef = useRef(null);

  useEffect(() => {
    fetchPreviewData();
    fetchSettings();
  }, [bookId]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchPreviewData = async () => {
    try {
      const response = await axios.get(`${API}/admin/books/${bookId}/publication-preview`);
      setData(response.data);
    } catch (error) {
      toast.error('Kon nie publikasie voorskou laai nie');
      navigate('/admin/books');
    } finally {
      setLoading(false);
    }
  };

  // Handle cover replacement
  const handleReplaceCover = async () => {
    if (!newCoverFile) {
      toast.error('Kies asseblief \'n voorblad lêer');
      return;
    }
    
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('cover', newCoverFile);
      
      await axios.post(`${API}/admin/books/${bookId}/replace-cover`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Voorblad suksesvol vervang!');
      setShowReplaceCoverDialog(false);
      setNewCoverFile(null);
      
      // Refresh the preview data
      await fetchPreviewData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kon nie voorblad vervang nie';
      toast.error(errorMsg);
      console.error('Replace cover error:', error);
    } finally {
      setUploadingCover(false);
    }
  };

  // Handle book file replacement
  const handleReplaceBookFile = async () => {
    if (!newBookFile) {
      toast.error('Kies asseblief \'n boek lêer');
      return;
    }
    
    // Validate file extension on frontend for better UX
    const validExtensions = ['.pdf', '.docx', '.epub'];
    const fileName = newBookFile.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExtension) {
      toast.error('Ongeldige lêerformaat. Kies asseblief \'n .PDF, .DOCX of .EPUB lêer.');
      return;
    }
    
    setUploadingBook(true);
    try {
      const formData = new FormData();
      formData.append('book_file', newBookFile);
      
      const response = await axios.post(`${API}/admin/books/${bookId}/replace-book-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Boek lêer suksesvol opgelaai en omgeskakel na EPUB!');
      setShowReplaceBookDialog(false);
      setNewBookFile(null);
      
      // Refresh the preview data
      await fetchPreviewData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kon nie boek lêer oplaai nie';
      toast.error(errorMsg);
      console.error('Replace book file error:', error);
    } finally {
      setUploadingBook(false);
    }
  };

  const handleRecalculatePreview = async () => {
    try {
      toast.info('Herbereken voorskou...');
      await axios.post(`${API}/admin/books/${bookId}/recalculate-preview`);
      toast.success('Voorskou herbereken! Herlaai om nuwe voorskou te sien.');
      // Refresh the data
      fetchPreviewData();
    } catch (error) {
      toast.error('Kon nie voorskou herbereken nie');
    }
  };


  const copyToClipboard = (text, label) => {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    // Make it invisible but still accessible
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success(`✓ ${label} gekopieer!`, { duration: 2000 });
      } else {
        toast.error('Kon nie kopieer nie. Probeer weer.', { duration: 3000 });
      }
    } catch (err) {
      document.body.removeChild(textArea);
      console.error('Copy failed:', err);
      toast.error('Kon nie kopieer nie. Gebruik Ctrl+C handmatig.', { duration: 3000 });
    }
  };

  const copyAllMetadata = async () => {
    const metadata = {
      'Boek Naam': data.book.boek_naam,
      'Skrywer': data.book.skrywer_naam,
      'Genre': data.book.genre,
      'Ouderdomsgroep': data.book.ouderdomsgroep,
      'Woordtelling': data.book.word_count,
      'Bladsy Telling': data.book.page_count,
      'Muntstuk Waarde': data.book.coin_value,
      'Blurb': data.book.blurb,
      'Voorskou (12%)': data.book.preview_text,
    };

    const formattedText = Object.entries(metadata)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n\n');

    await copyToClipboard(formattedText, 'Alle metadata');
  };

  const handleDownloadPackage = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const url = `${API}/admin/books/${bookId}/export-package-download?token=${token}`;
      window.open(url, '_blank');
      toast.success('Eksport pakket word afgelaai...', { duration: 5000 });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Kon nie pakket aflaai nie: ' + error.message);
    } finally {
      setDownloading(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await axios.post(`${API}/admin/books/${bookId}/publish`);
      
      setShowPublishDialog(false);
      
      toast.success('Boek gepubliseer! 🎉', {
        duration: 4000,
        description: 'Die leeswebwerf sal nou oopmaak om die boek by te voeg.'
      });
      
      // Always open the reading website in a new tab
      const readingWebsiteUrl = settings.reading_website_url || 'https://biblioteek.louisscheepers.com/admin/books/create';
      
      setTimeout(() => {
        window.open(readingWebsiteUrl, '_blank');
        toast.info('💡 Kopieer die metadata van hierdie bladsy en plak in die leeswebwerf vorm.', {
          duration: 6000
        });
      }, 1000);
      
      // Don't navigate away - let user stay on preview page to copy info
      // User can manually go back when done
    } catch (error) {
      toast.error('Kon nie publiseer nie');
      setPublishing(false);
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!data) return null;

  const { book, writer, proofreader, book_file_info, cover_file_info } = data;

  const MetadataRow = ({ label, value, copyable = true }) => (
    <div className="py-3 border-b border-gray-200 last:border-0">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{label}</p>
          <p className="text-base text-gray-900 whitespace-pre-wrap">{value || 'N/A'}</p>
        </div>
        {copyable && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => copyToClipboard(value?.toString() || '', label)}
            className="flex-shrink-0"
          >
            <Copy className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/books')}>
            <ArrowLeft className="h-5 w-5 mr-2" />
            Terug
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Publikasie Voorskou</h2>
            <p className="text-sm text-gray-600 mt-1">{book.boek_naam}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {book.status === 'Gepubliseer' ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">Gepubliseer</span>
            </div>
          ) : (
            <Button
              onClick={() => setShowPublishDialog(true)}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
              disabled={publishing}
            >
              {publishing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publiseer...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Publiseer Boek
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cover Image Preview */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Voorblad</CardTitle>
          </CardHeader>
          <CardContent>
            {cover_file_info.exists ? (
              <img
                src={coverUrl(bookId, COVER_WIDTH.detail)}
                alt={book.boek_naam}
                loading="lazy"
                width={400}
                height={600}
                className="w-full rounded-lg shadow-md"
                onError={(e) => {
                  console.error('Cover image failed to load for book:', bookId);
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="300"%3E%3Crect fill="%23ddd" width="200" height="300"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%23999"%3ELaai Fout%3C/text%3E%3C/svg%3E';
                }}
              />
            ) : (
              <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                <p className="text-gray-500">Voorblad nie gevind nie</p>
              </div>
            )}
            <div className="mt-4 text-xs text-gray-600">
              <p>{cover_file_info.filename}</p>
              {cover_file_info.exists ? (
                <p>{(cover_file_info.size / 1024).toFixed(2)} KB</p>
              ) : (
                <p className="text-red-500">Lêer bestaan nie</p>
              )}
            </div>
            
            {/* Replace Cover Button */}
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => setShowReplaceCoverDialog(true)}
            >
              <Upload className="h-4 w-4 mr-2" />
              Herlaai Voorblad
            </Button>
          </CardContent>
        </Card>

        {/* Metadata */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Boek Metadata</CardTitle>
            <Button variant="outline" onClick={copyAllMetadata}>
              <Copy className="h-4 w-4 mr-2" />
              Kopieer Alles
            </Button>
          </CardHeader>
          <CardContent className="space-y-0">
            <MetadataRow label="Boek Naam" value={book.boek_naam} />
            <MetadataRow label="Skrywer" value={book.skrywer_naam} />
            <MetadataRow label="Genre" value={book.genre} />
            <MetadataRow label="Ouderdomsgroep" value={book.ouderdomsgroep} />
            <MetadataRow label="Woordtelling" value={book.word_count} />
            <MetadataRow label="Bladsy Telling" value={book.page_count} />
            <MetadataRow label="Muntstuk Waarde" value={book.coin_value} />
          </CardContent>
        </Card>
      </div>

      {/* Blurb */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Blurb</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(book.blurb, 'Blurb')}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-gray-900 whitespace-pre-wrap">{book.blurb}</p>
        </CardContent>
      </Card>

      {/* Preview Text */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Voorskou Teks (12%)</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRecalculatePreview}
              >
                <Loader2 className="h-4 w-4 mr-2" />
                Herbereken
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(book.preview_text, 'Voorskou Teks')}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-gray-900 whitespace-pre-wrap text-sm leading-relaxed max-h-96 overflow-y-auto">
            {book.preview_text}
          </p>
        </CardContent>
      </Card>

      {/* File Information & Download */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lêers</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">EPUB Boek Lêer</p>
              <p className="text-sm text-gray-600">{book_file_info.filename || 'Geen lêer'}</p>
              <p className="text-xs text-gray-500">
                {book_file_info.is_cloudinary 
                  ? '☁️ Gestoor in Cloudinary (Permanent)' 
                  : `${(book_file_info.size / 1024 / 1024).toFixed(2)} MB`}
              </p>
              {book_file_info.exists && book_file_info.is_cloudinary && (
                <p className="text-xs text-green-600 mt-1">✓ Veilig gestoor - sal nie verlore gaan nie</p>
              )}
              {!book_file_info.exists && (
                <p className="text-xs text-red-500 mt-1">⚠️ Lêer ontbreek - herlaai asseblief</p>
              )}
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowReplaceBookDialog(true)}
            >
              <Upload className="h-4 w-4 mr-1" />
              Herlaai
            </Button>
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Voorblad Beeld</p>
              <p className="text-sm text-gray-600">{cover_file_info.filename}</p>
              <p className="text-xs text-gray-500">{(cover_file_info.size / 1024).toFixed(2)} KB</p>
            </div>
          </div>
          <div className="space-y-2">
            <Button
              onClick={() => {
                const url = `${API}/files/preview/${bookId}`;
                window.open(url, '_blank');
                toast.success('EPUB lêer word afgelaai!');
              }}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Laai EPUB Voorskou Af
            </Button>
            <Button
              onClick={handleDownloadPackage}
              disabled={downloading}
              className="w-full"
              variant="outline"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Laai af...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Laai Eksport Pakket Af (ZIP)
                </>
              )}
            </Button>
            <p className="text-xs text-gray-500 text-center">
              ZIP bevat: EPUB lêer, voorblad beeld, en metadata.json
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Additional Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Bykomende Inligting</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {writer && (
            <div>
              <p className="text-sm font-medium text-gray-600">Skrywer Profiel</p>
              <p className="text-gray-900">{writer.voornaam} {writer.van} ({writer.email})</p>
            </div>
          )}
          {proofreader && (
            <div className="mt-3">
              <p className="text-sm font-medium text-gray-600">Proefleeser</p>
              <p className="text-gray-900">{proofreader.voornaam} {proofreader.van} ({proofreader.email})</p>
            </div>
          )}
          <div className="mt-3">
            <p className="text-sm font-medium text-gray-600">Ingedien Datum</p>
            <p className="text-gray-900">{new Date(book.submission_date).toLocaleDateString('af-ZA')}</p>
          </div>
          {book.publication_date && (
            <div className="mt-3">
              <p className="text-sm font-medium text-gray-600">Publikasie Datum</p>
              <p className="text-gray-900">{new Date(book.publication_date).toLocaleDateString('af-ZA')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Publish Confirmation Dialog */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publiseer Boek?</DialogTitle>
            <DialogDescription className="space-y-3">
              <p className="text-base">Is jy seker jy wil "<strong>{book.boek_naam}</strong>" publiseer?</p>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-semibold text-orange-900">📋 Wat sal gebeur:</p>
                <ol className="text-sm text-orange-800 list-decimal list-inside space-y-1">
                  <li>Boek word gemerk as "Gepubliseer" in hierdie sisteem</li>
                  <li>Jou leeswebwerf sal oopmaak in 'n nuwe tab</li>
                  <li>Kopieer die metadata hier en plak in die vorm daar</li>
                  <li>Laai die ZIP pakket af en gebruik die lêers</li>
                </ol>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>
              Kanselleer
            </Button>
            <Button
              onClick={handlePublish}
              disabled={publishing}
              className="bg-gradient-to-r from-green-500 to-green-600"
            >
              {publishing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publiseer...
                </>
              ) : (
                'Ja, Publiseer & Maak Oop'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace Cover Dialog */}
      <Dialog open={showReplaceCoverDialog} onOpenChange={setShowReplaceCoverDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Herlaai Voorblad</DialogTitle>
            <DialogDescription>
              Kies 'n nuwe voorblad prent vir hierdie boek. Die prent sal na Cloudinary opgelaai word vir permanente stoor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cover-file">Voorblad Prent</Label>
              <Input
                id="cover-file"
                type="file"
                accept="image/*"
                ref={coverInputRef}
                onChange={(e) => setNewCoverFile(e.target.files[0])}
              />
              {newCoverFile && (
                <p className="text-sm text-gray-500">
                  Gekies: {newCoverFile.name} ({(newCoverFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
            {newCoverFile && (
              <div className="flex justify-center">
                <img
                  src={URL.createObjectURL(newCoverFile)}
                  alt="Preview"
                  className="max-h-48 rounded shadow"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowReplaceCoverDialog(false);
                setNewCoverFile(null);
              }}
            >
              Kanselleer
            </Button>
            <Button
              onClick={handleReplaceCover}
              disabled={uploadingCover || !newCoverFile}
            >
              {uploadingCover ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Oplaai...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Vervang Voorblad
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace Book File Dialog */}
      <Dialog open={showReplaceBookDialog} onOpenChange={setShowReplaceBookDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Herlaai Boek Lêer</DialogTitle>
            <DialogDescription>
              Laai die oorspronklike boek lêer (PDF, DOCX of EPUB) op. Dit sal outomaties na EPUB omgeskakel word.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="book-file">Boek Lêer (PDF, DOCX of EPUB)</Label>
              <Input
                id="book-file"
                type="file"
                accept=".pdf,.docx,.epub,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/epub+zip"
                ref={bookInputRef}
                onChange={(e) => setNewBookFile(e.target.files[0])}
              />
              {newBookFile && (
                <p className="text-sm text-green-600">
                  ✓ Gekies: {newBookFile.name} ({(newBookFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-800">
                <strong>Let wel:</strong> Die lêer sal outomaties na EPUB formaat omgeskakel word. 
                Maak seker jou boek het duidelike hoofstukopskrifte (bv. "Hoofstuk 1").
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowReplaceBookDialog(false);
                setNewBookFile(null);
              }}
            >
              Kanselleer
            </Button>
            <Button
              onClick={handleReplaceBookFile}
              disabled={uploadingBook || !newBookFile}
            >
              {uploadingBook ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Omskakel na EPUB...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Laai Op & Skakel Om
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreviewPublication;
